
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateLetterPDF } from "@/utils/pdfGenerator";
import { stripHtmlTags } from "@/utils/htmlUtils";
import { LETTER_SHARE_ISSUE, clipboardShareText, shareTokenFromRpc } from "@/utils/letterShare";

export function useLetterActions(onRefetch: () => void) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [letterToDelete, setLetterToDelete] = useState<string | null>(null);

  const handleView = (id: string) => {
    navigate(`/admin/carta/${id}`);
  };

  const handleShare = async (id: string) => {
    try {
      const { data, error } = await supabase.rpc(LETTER_SHARE_ISSUE, { p_letter_id: id });
      const token = shareTokenFromRpc(id, { data, error });
      const shareText = token ? clipboardShareText(window.location.origin, id, token) : null;
      if (!shareText) {
        toast({
          variant: "destructive",
          title: "Link indisponível",
          description: "Não foi possível gerar o link de compartilhamento.",
        });
        return;
      }

      await navigator.clipboard.writeText(shareText);

      toast({
        title: "Link copiado!",
        description: "O link da carta foi copiado para a área de transferência.",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Erro ao compartilhar",
        description: "Não foi possível copiar o link da carta.",
      });
    }
  };

  const handleDownload = async (id: string) => {
    try {
      console.log("Fazendo download da carta com ID:", id);
      
      // Buscar o conteúdo da carta
      const { data: letter, error } = await supabase
        .from("letters")
        .select("content, delivery_date")
        .eq("id", id)
        .eq("sent", true)
        .eq("payment_status", "paid")
        .maybeSingle();

      console.log("Dados da carta para download:", letter);

      if (error) {
        console.error("Erro ao buscar carta:", error);
        toast({
          variant: "destructive",
          title: "Erro no download",
          description: "Não foi possível buscar os dados da carta.",
        });
        return;
      }

      if (!letter) {
        console.log("Carta não encontrada para download");
        toast({
          variant: "destructive",
          title: "Erro no download",
          description: "Carta não encontrada ou não disponível.",
        });
        return;
      }

      // Converter HTML para texto limpo para o PDF
      const cleanContent = stripHtmlTags(letter.content);
      const deliveryDate = new Date(letter.delivery_date + "T00:00:00").toLocaleDateString("pt-BR");

      console.log("Gerando PDF da carta");
      
      // Gerar PDF
      await generateLetterPDF(cleanContent, deliveryDate);

      toast({
        title: "Download iniciado",
        description: "Sua carta será baixada em formato PDF.",
      });
    } catch (error) {
      console.error("Erro no download:", error);
      toast({
        variant: "destructive",
        title: "Erro no download",
        description: "Não foi possível baixar a carta.",
      });
    }
  };

  const confirmDelete = (id: string) => {
    setLetterToDelete(id);
  };

  const handleDelete = async () => {
    if (!letterToDelete) return;
    
    try {
      const { error } = await supabase
        .from("letters")
        .delete()
        .eq("id", letterToDelete);
        
      if (error) throw error;
      
      toast({
        title: "Carta excluída",
        description: "A carta foi removida com sucesso.",
      });
      
      onRefetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir carta",
        description: "Por favor, tente novamente mais tarde.",
      });
    } finally {
      setLetterToDelete(null);
    }
  };

  return {
    letterToDelete,
    setLetterToDelete,
    handleView,
    handleShare,
    handleDownload,
    confirmDelete,
    handleDelete
  };
}
